import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useI18n, formatDateWithMonthName } from '../i18n';

export default function PrivacyPolicy({ onBack }) {
  const { t, lang } = useI18n();
  const formattedDate = formatDateWithMonthName(new Date(), lang);
  return (
    <div className="min-h-screen">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">{t('common.back')}</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 sm:py-12">
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('privacy.title')}
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            {t('privacy.lastUpdated', { date: formattedDate })}
          </p>

          <div className="prose prose-gray max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section1Title')}</h2>
              <p className="leading-relaxed" dangerouslySetInnerHTML={{ __html: t('privacy.section1Body') }} />
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section2Title')}</h2>
              <p className="leading-relaxed mb-3">{t('privacy.section2Intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section2Item1') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section2Item2') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section2Item3') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section2Item4') }} />
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section3Title')}</h2>
              <p className="leading-relaxed mb-3">{t('privacy.section3Intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>{t('privacy.section3Item1')}</li>
                <li>{t('privacy.section3Item2')}</li>
                <li>{t('privacy.section3Item3')}</li>
                <li>{t('privacy.section3Item4')}</li>
                <li>{t('privacy.section3Item5')}</li>
                <li>{t('privacy.section3Item6')}</li>
                <li>{t('privacy.section3Item7')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section4Title')}</h2>
              <p className="leading-relaxed mb-3">{t('privacy.section4Intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section4Item1') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section4Item2') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section4Item3') }} />
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section5Title')}</h2>
              <p className="leading-relaxed" dangerouslySetInnerHTML={{ __html: t('privacy.section5Body1') }} />
              <p className="leading-relaxed mt-3">{t('privacy.section5Body2')}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section6Title')}</h2>
              <p className="leading-relaxed">{t('privacy.section6Body')}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section7Title')}</h2>
              <p className="leading-relaxed mb-3">{t('privacy.section7Intro')}</p>
              <ul className="list-disc pl-6 space-y-2">
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item1') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item2') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item3') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item4') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item5') }} />
                <li dangerouslySetInnerHTML={{ __html: t('privacy.section7Item6') }} />
              </ul>
              <p className="leading-relaxed mt-3" dangerouslySetInnerHTML={{ __html: t('privacy.section7Contact') }} />
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section8Title')}</h2>
              <p className="leading-relaxed">{t('privacy.section8Body')}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section9Title')}</h2>
              <p className="leading-relaxed">{t('privacy.section9Body')}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section10Title')}</h2>
              <p className="leading-relaxed">{t('privacy.section10Body')}</p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold">{t('about.companyName')}</p>
                <p>{t('about.companyCnpj')}</p>
                <p>{t('privacy.contactEmailLabel')} <a href="mailto:privacidade@dojomojo.com.br" className="text-lavender-600 hover:text-lavender-700 underline">privacidade@dojomojo.com.br</a></p>
                <p>{t('privacy.contactSupportLabel')} <a href="mailto:contato@dojomojo.com.br" className="text-lavender-600 hover:text-lavender-700 underline">contato@dojomojo.com.br</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('privacy.section11Title')}</h2>
              <p className="leading-relaxed">{t('privacy.section11Body')}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
